import request from '@/utils/request'
import { buildGeodesiaMapLayerParams } from '@/utils/geodesiaMapLayers'

const baseURL = '/geodesia'

export default {
  // Get paginated list of geodesia projects with filters
  list(params = {}) {
    return request({
      url: baseURL,
      method: 'get',
      params,
    })
  },

  // Geodesia Records (statuses per project survey code)
  listGeodesiaRecords(params = {}) {
    return request({
      url: `${baseURL}/records`,
      method: 'get',
      params,
    })
  },

  createGeodesiaRecord(data) {
    return request({
      url: `${baseURL}/records`,
      method: 'post',
      data,
    })
  },

  getGeodesiaRecord(id) {
    return request({
      url: `${baseURL}/records/${id}`,
      method: 'get',
    })
  },

  // IMPORTANT: update uses POST (not PUT)
  updateGeodesiaRecord(id, data) {
    return request({
      url: `${baseURL}/records/${id}`,
      method: 'post',
      data,
    })
  },

  deleteGeodesiaRecord(id) {
    return request({
      url: `${baseURL}/records/${id}`,
      method: 'delete',
    })
  },

  importGeodesiaRecordsFromExcel(file) {
    const formData = new FormData()
    // Backend expects multipart/form-data field named `file`
    formData.append('file', file)

    return request({
      url: `${baseURL}/records/import-excel`,
      method: 'post',
      data: formData,
    })
  },

  // Download raw files for selected projects
  downloadRawFiles(projectIds) {
    return request({
      url: `${baseURL}/download-raw-files`,
      method: 'post',
      data: {
        project_ids: projectIds,
      },
      responseType: 'blob',
      rawResponse: true,
      validateStatus: status => (status >= 200 && status < 300) || [404, 422, 500].includes(status),
      headers: {
        Accept: 'application/zip, application/octet-stream, application/json;q=0.9, */*;q=0.8',
      },
    })
  },

  // Upload ZIP file with RW5 files
  uploadZipRw5Files(zipFile) {
    const formData = new FormData()
    formData.append('zip_file', zipFile)
    return request({
      url: `${baseURL}/upload-zip-rw5-files`,
      method: 'post',
      data: formData,
    })
  },

  // Assign RW5 file to project
  assignMediaToProject(projectId, rw5FileId) {
    return request({
      url: `${baseURL}/assign-media-to-project`,
      method: 'post',
      data: {
        project_id: projectId,
        rw5_file_id: rw5FileId,
      },
    })
  },
  // Assign RW5 file to project
  assignMediaToProjectBySurveyCoding(surveyCoding, rw5FileId) {
    return request({
      url: `${baseURL}/assign-media-to-project-by-survey-coding`,
      method: 'post',
      data: {
        survey_coding: surveyCoding,
        rw5_file_id: rw5FileId,
      },
    })
  },

  unassignProjectFromJxl(projectId) {
    return request({
      url: `${baseURL}/unassign-project-from-jxl`,
      method: 'post',
      data: {
        project_id: projectId,
      },
    })
  },

  // Get list of RW5 files
  getRw5Files(params = {}) {
    return request({
      url: `${baseURL}/rw5-files`,
      method: 'get',
      params,
    })
  },

  // Mark RW5 files as moved
  markRw5FilesAsMoved(rw5FileIds) {
    return request({
      url: `${baseURL}/rw5-files/mark-as-moved`,
      method: 'post',
      data: {
        rw5_file_ids: rw5FileIds,
      },
    })
  },

  // Import KMZ file
  importKmzFile(kmzFile, projectId = null) {
    const formData = new FormData()
    formData.append('kmz_file', kmzFile)
    if (projectId) {
      formData.append('project_id', String(projectId))
    }
    return request({
      url: `${baseURL}/import-kmz-file`,
      method: 'post',
      data: formData,
    })
  },

  // Import KML file
  importKmlFile(kmlFile, projectId = null) {
    const formData = new FormData()
    formData.append('kml_file', kmlFile)
    if (projectId) {
      formData.append('project_id', String(projectId))
    }
    return request({
      url: `${baseURL}/import-kml-file`,
      method: 'post',
      data: formData,
    })
  },

  // Import DXF file
  importDxfFile(dxfFile, projectId = null) {
    const formData = new FormData()
    formData.append('dxf_file', dxfFile)
    if (projectId) {
      formData.append('project_id', String(projectId))
    }
    return request({
      url: `${baseURL}/import-dxf-file`,
      method: 'post',
      data: formData,
    })
  },

  // Import shapefile ZIP archive
  importShpFile(shpFile, projectId = null) {
    const formData = new FormData()
    formData.append('shp_file', shpFile)
    if (projectId) {
      formData.append('project_id', String(projectId))
    }
    return request({
      url: `${baseURL}/import-shp-file`,
      method: 'post',
      data: formData,
    })
  },

  // Import HTML survey report
  importHtmlFile(htmlFile, projectId = null) {
    const formData = new FormData()
    formData.append('html_file', htmlFile)
    if (projectId) {
      formData.append('project_id', String(projectId))
    }
    return request({
      url: `${baseURL}/import-html-file`,
      method: 'post',
      data: formData,
    })
  },

  // Import Geodesia / Trimble Excel sheet (.xlsx / .xls)
  importGeodesiaSheetFile(sheetFile, projectId = null) {
    const formData = new FormData()
    formData.append('geodesia_sheet_file', sheetFile)
    if (projectId) {
      formData.append('project_id', String(projectId))
    }
    return request({
      url: `${baseURL}/import-geodesia-sheet-file`,
      method: 'post',
      data: formData,
    })
  },

  getMapLayer(url, params = {}, config = {}) {
    return request({
      url,
      method: 'get',
      params: buildGeodesiaMapLayerParams(params),
      ...(config.cancelToken ? { cancelToken: config.cancelToken } : {}),
    })
  },

  // Get VRS coordinates as map points
  getVrsCoordinatesMapPoints(params = {}, config = {}) {
    return this.getMapLayer(`${baseURL}/vrs-coordinates/map-points`, params, config)
  },

  // Import JXL file
  importJxlFile(jxlFile, projectId = null) {
    const formData = new FormData()
    formData.append('jxl_file', jxlFile)
    if (projectId) {
      formData.append('project_id', String(projectId))
    }
    return request({
      url: `${baseURL}/import-jxl-file`,
      method: 'post',
      data: formData,
    })
  },

  // Import RW5 file
  importRw5File(rw5File, projectId = null) {
    const formData = new FormData()
    formData.append('rw5_file', rw5File)
    if (projectId) {
      formData.append('project_id', String(projectId))
    }
    return request({
      url: `${baseURL}/import-rw5-file`,
      method: 'post',
      data: formData,
    })
  },

  // Import RAW file
  importRawFile(rawFile, projectId = null) {
    const formData = new FormData()
    formData.append('raw_file', rawFile)
    if (projectId) {
      formData.append('project_id', String(projectId))
    }
    return request({
      url: `${baseURL}/import-raw-file`,
      method: 'post',
      data: formData,
    })
  },

  // Import RAW or RW5 file
  importRawOrRw5File(rawFile) {
    const formData = new FormData()
    formData.append('raw_file', rawFile)
    return request({
      url: `${baseURL}/import-raw-or-rw5-file`,
      method: 'post',
      data: formData,
    })
  },

  // JXL points only — no RW5/RAW/polygons/baselines
  getJxlCoordinatesMapPoints(params = {}, config = {}) {
    return this.getMapLayer(`${baseURL}/jxl-coordinates/map-points`, params, config)
  },

  getRw5CoordinatesMapPoints(params = {}, config = {}) {
    return this.getMapLayer(`${baseURL}/rw5-coordinates/map-points`, params, config)
  },

  getRawCoordinatesMapPoints(params = {}, config = {}) {
    return this.getMapLayer(`${baseURL}/raw-coordinates/map-points`, params, config)
  },

  getJxlPolygonsMapFeatures(params = {}, config = {}) {
    return this.getMapLayer(`${baseURL}/jxl-polygons/map-features`, params, config)
  },

  getJxlBaselineLinesMapFeatures(params = {}, config = {}) {
    return this.getMapLayer(`${baseURL}/jxl-baseline-lines/map-features`, params, config)
  },

  getGeodesiaMapLayer(layerKey, params = {}, config = {}) {
    const fetchers = {
      vrsCoordinates: this.getVrsCoordinatesMapPoints,
      jxlCoordinates: this.getJxlCoordinatesMapPoints,
      jxlRw5Coordinates: this.getRw5CoordinatesMapPoints,
      jxlRawCoordinates: this.getRawCoordinatesMapPoints,
      jxlPolygons: this.getJxlPolygonsMapFeatures,
      jxlBaselineLines: this.getJxlBaselineLinesMapFeatures,
    }
    const fetcher = fetchers[layerKey]
    if (typeof fetcher !== 'function') {
      return Promise.reject(new Error(`Unknown geodesia map layer: ${layerKey}`))
    }
    return fetcher.call(this, params, config)
  },

  // Get JXL files from storage directory
  getJxlFilesFromStorage(directory = null) {
    const params = {}
    if (directory) {
      params.directory = directory
    }
    return request({
      url: `${baseURL}/jxl-files-from-storage`,
      method: 'get',
      params,
    })
  },

  // Process JXL files from storage directory
  processJxlFilesFromStorage(directory = null, projectId = null) {
    const data = {}
    if (directory) {
      data.directory = directory
    }
    if (projectId) {
      data.project_id = projectId
    }
    return request({
      url: `${baseURL}/process-jxl-files-from-storage`,
      method: 'post',
      data,
    })
  },

  // Assign JXL file to project by survey coding
  // surveyCodings can be a string (single) or array (multiple)
  assignJxlToProjectBySurveyCoding(surveyCodings, jxlFileId = null, jxlCoordinateId = null) {
    const data = {
      survey_codings: Array.isArray(surveyCodings) ? surveyCodings : [surveyCodings],
    }
    if (jxlFileId) {
      data.jxl_file_id = jxlFileId
    }
    if (jxlCoordinateId) {
      data.jxl_coordinate_id = jxlCoordinateId
    }
    return request({
      url: `${baseURL}/assign-jxl-to-project-by-survey-coding`,
      method: 'post',
      data,
    })
  },

  unAssignJxlToProjectBySurveyCoding(surveyCoding) {
    return request({
      url: `${baseURL}/unassign-jxl-from-project-by-survey-coding`,
      method: 'post',
      data: {
        survey_coding: surveyCoding,
      },
    })
  },
  deleteJxlFile(jxlCoordinateId) {
    return request({
      url: `${baseURL}/delete-jxl-file`,
      method: 'post',
      data: {
        jxl_coordinate_id: jxlCoordinateId,
      },
    })
  },


  // Assign JXL coordinates to project
  assignJxlCoordinatesToProject(projectId, jxlCoordinateIds) {
    return request({
      url: `${baseURL}/assign-jxl-coordinates-to-project`,
      method: 'post',
      data: {
        project_id: projectId,
        jxl_coordinate_ids: Array.isArray(jxlCoordinateIds) ? jxlCoordinateIds : [jxlCoordinateIds],
      },
    })
  },

  // Get JXL coordinates by project ID
  getJxlCoordinatesByProjectId(projectId) {
    return request({
      url: `${baseURL}/jxl-coordinates/by-project`,
      method: 'get',
      params: {
        project_id: projectId,
      },
    })
  },

  // Download projects files with coordinates
  downloadProjectsFilesWithCoordinates(params) {
    return request({
      url: `${baseURL}/download-projects-files-with-coordinates`,
      method: 'post',
      data: params,
      responseType: 'blob',
      rawResponse: true,
      validateStatus: status => (status >= 200 && status < 300) || [404, 422, 500].includes(status),
      headers: {
        Accept: 'application/zip, application/octet-stream, application/json;q=0.9, */*;q=0.8',
      },
    })
  },

  getProjectSurveyFile(projectId, fileExtension) {
    return request({
      url: `${baseURL}/project-survey-file`,
      method: 'post',
      data: {
        project_id: projectId,
        file_extension: String(fileExtension || '').replace(/^\./, '').toLowerCase(),
      },
    })
  },
}
